import { useEffect, useState } from 'react';

import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useUserStrategies } from '../../../context/UserStrategies';

import { DatePicker, DropdownMenu } from '../../form';
import { ChartLine } from '../../Strategies/ChartLine';

import { ActionButton, Icon } from '../../UI';
import { StrategyLabel } from '../../Strategies/StrategyLabel';

import { INTERVALS, useIntervals } from '../../../hooks/useIntervals';
import { useToggle } from '../../../hooks/useToggle';

import {
  getDataForResponsiveLine,
  getEndDate,
  getStartEndDates,
  getSymbol,
} from '../../../helpers/functions';

import { Currency } from '../../../types/common';
import CalendarRange from '../../CalendarRange/CalendarRange';
import ScrollArrows from '../Contribution/ScrollArrows';
import { Checkbox } from '../../form';
import { PlaceholderStrategy } from '../../Strategies/Placeholder';
import { GridWidget } from '../types';
import { TTickerCurrency } from '../../../types/model';
import { CurrencyCodeEnum } from '../../../constants/enums';

export const StrategiesPerformance = ({ width, height }: GridWidget) => {
  const currency = CurrencyCodeEnum.USD;
  const { t } = useTranslation();
  const { strategies } = useUserStrategies();

  const {
    toggleState: isBlackSwanToggleActive,
    ToggleComponent: BlackSwanToggle,
    handleToggleState: setBlackSwanToggleState,
  } = useToggle({ description: t('widget.blackSwan') });

  const [[intervalStartDate, intervalEndDate], setDates] = useState([
    moment().subtract(1, 'year').toDate(),
    getEndDate(),
  ]);

  const [showCalendar, setShowCalendar] = useState(false);

  const [dateRangeState, setDateRangeState] = useState([
    {
      intervalStartDate,
      intervalEndDate,
      key: 'selection',
    },
  ]);

  const {
    interval,
    IntervalSelector,
    handleInterval: resetInterval,
  } = useIntervals();

  const [selectedStrategies, setSelectedStrategies] = useState(
    strategies.slice(0, 2).map(({ id }) => id)
  );

  useEffect(() => {
    if (interval !== 'VAR_24H') {
      const { endDate, startDate } = getStartEndDates(interval);

      setDates([startDate, endDate]);
    }
  }, [interval]);

  const { dropdownItems, dropdownItemsData } = strategies
    .filter(({ id }) => !selectedStrategies.includes(id))
    .reduce(
      ({ dropdownItems, dropdownItemsData }, { id, name }) => {
        dropdownItems.push(name);
        dropdownItemsData.push(id);

        return { dropdownItems, dropdownItemsData };
      },
      { dropdownItems: [] as string[], dropdownItemsData: [] as string[] }
    );

  const isDropdownDisabled =
    selectedStrategies.length === 5 ||
    selectedStrategies.length === strategies.length;

  const data = strategies
    .filter(({ id }) => selectedStrategies.includes(id))
    .map(({ color, createdAt, strategyAssets: { items }, name }) =>
      getDataForResponsiveLine({
        assets: items,
        color,
        currency: currency.toLocaleLowerCase() as TTickerCurrency,
        endDate: moment(intervalEndDate).format('YYYY-MM-DD'),
        name,
        startDate: moment(intervalStartDate).format('YYYY-MM-DD'),
        strategyCreatedAt: createdAt,
        usePercentage: selectedStrategies.length > 1,
        useUnit: true,
      })
    );

  const handleCalendar = (newDates: [Date, Date]) => {
    resetInterval();
    setDates(newDates);
  };

  const handleSelectedStrategies = ({
    id: selectedId,
    add,
  }: {
    id: string;
    add: boolean;
  }) => {
    setSelectedStrategies(
      add
        ? [...selectedStrategies, selectedId]
        : selectedStrategies.filter((id) => selectedId !== id)
    );
  };

  const handleDateRangePickerChange = (item) => {
    const { selection } = item;

    setDateRangeState([selection]);
    setDates([selection.startDate, selection.endDate]);
  };

  const dropdownItemsWidth1 = [
    {
      item: interval,
      hideItemOnClick: false,
      dropdownItems: [...INTERVALS],
      handleClick: resetInterval,
    },

    {
      item: (
        <div
          className="flex items-center gap-3 w-full"
          onClick={() => setShowCalendar((pr) => !pr)}
        >
          <Icon svgName="cal" />
          <span className="mr-auto capitalize-first">
            {t('common.custom', { value: t('common.date') })}
          </span>
        </div>
      ),
      withBorder: true,
    },

    <div
      className="flex items-center gap-3 w-full"
      onClick={() => setBlackSwanToggleState()}
    >
      <span className="mr-auto">{t('widget.blackSwan')}</span>
      {isBlackSwanToggleActive && (
        <Checkbox shape="circle" isCheck={true} size="small" />
      )}
    </div>,
  ];

  return strategies.length < 2 ? (
    <PlaceholderStrategy
      title={t('placeholders.compareStrategies.title')}
      subtitle={t('placeholders.compareStrategies.subtitle')}
    />
  ) : (
    <>
      {showCalendar ? (
        <CalendarRange
          goBack={() => setShowCalendar(false)}
          ranges={dateRangeState}
          startDate={intervalStartDate}
          endDate={intervalEndDate}
          handleDateRangeChange={handleDateRangePickerChange}
        />
      ) : (
        <>
          {/* <CompareTutorial /> */}
          {/* ADD STAREGY BUTTON */}
          <div
            className={`
              flex
              items-center
              justify-between
              border-b
              border-grey-30
              dark:border-grey-70
              h-14
              pl-5
          `}
          >
            <div className={'flex items-center w-full'}>
              <DropdownMenu
                dropdownClassName={`
                  flex
                  items-center
                  ${isDropdownDisabled && 'text-grey-30'}
                `}
                dropdownTooltipClassName={`
                  bg-white
                  border-solid
                  border
                  border-grey-30
                  dark:border-grey-70
                `}
                dropdownTooltipXOffset={-10}
                dropdownTooltipYOffset={10}
                dropdownItems={dropdownItems}
                dropdownItemsData={dropdownItemsData}
                handleClick={(name, id) =>
                  handleSelectedStrategies({ id, add: true })
                }
                isDropdownDisabled={isDropdownDisabled}
                disableCursorOverride={isDropdownDisabled}
              >
                <ActionButton
                  text={t('strategies.addStrategy')}
                  size={'large'}
                  isDisabled={isDropdownDisabled}
                />
              </DropdownMenu>

              {/* STATEGIES SELECTED */}
              <ScrollArrows className={'w-full'}>
                {strategies.map(
                  ({ id, name, color }) =>
                    selectedStrategies.includes(id) && (
                      <StrategyLabel
                        className={''}
                        key={id}
                        color={color}
                        name={name}
                        viewType="tag"
                        onClose={() =>
                          handleSelectedStrategies({ id, add: false })
                        }
                      />
                    )
                )}
              </ScrollArrows>
            </div>

            {width !== 1 ? (
              <div className="flex ml-auto h-full">
                {/* CALENDAR */}
                <div
                  className={`
                    flex
                    items-center
                    px-3
                    border-l
                    border-grey-30
                    dark:border-grey-70
                    h-full
                  `}
                >
                  <DatePicker
                    endDate={intervalEndDate}
                    hasBorderBottom={false}
                    isRangePicker={true}
                    id={'strategiesPerformanceDate'}
                    maxDate={new Date()}
                    size={'medium'}
                    startDate={intervalStartDate}
                    onChange={(range) => handleCalendar(range as [Date, Date])}
                  />
                </div>

                {/* INTERVAL SELECTOR */}
                <IntervalSelector className="border-l border-grey-30 dark:border-grey-70" />
              </div>
            ) : (
              <DropdownMenu
                dropdownClassName={`
                  flex
                  items-center
                  border
                  h-full
                  border-transparent
                  cursor-pointer
                  hover:border-black
                  hover:border
                  dark:border-grey-70
                  dark:hover:border-grey-30
                `}
                dropdownTooltipClassName={`
                  bg-white
                  pl-0
                  border-solid
                  border
                  border-grey-30
                `}
                dropdownItems={dropdownItemsWidth1}
              >
                <Icon svgName="options" className="w-12 h-12 py-4 pr-3" />
              </DropdownMenu>
            )}
          </div>

          {/* CHART */}
          {data.length === 0 ? (
            <div className="container mx-auto px-4 py-4 h-96 text-center">
              No data available!
            </div>
          ) : data.every((el) => el.data.length === 0) ? (
            // TODO find a better condition
            <div className="container mx-auto px-4 py-4 h-96 text-center">
              First data available after 24 hours from strategy creation!
            </div>
          ) : (
            <div className={`${height > 1 ? 'my-12 mr-8' : 'my-4 mr-4'}`}>
              <ChartLine
                canShowBlackSwan={isBlackSwanToggleActive}
                data={data}
                height={height > 1 ? 180 : 72}
                suffix={getSymbol(currency)}
                setBlackSwanToggleState={setBlackSwanToggleState}
                usePercentage={false}
                useVariation={true}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};
